<template>
  <div class="vpn-instructions">
    <div class="instruction-header">
      <font-awesome-icon icon="info-circle" class="info-icon" />
      <h5>Enable System-Level Always-on VPN</h5>
    </div>
    
    <div class="instruction-content">
      <p class="instruction-intro">
        For maximum security, enable Always-on VPN and Block connections without VPN in your Android settings:
      </p>
      
      <div class="instruction-steps">
        <div class="step">
          <div class="step-number">1</div>
          <div class="step-content">
            <h6>Open VPN Settings</h6>
            <p>Go to Settings → Network & Internet → VPN</p>
            <button @click="openVpnSettings" class="quick-action-btn">
              <font-awesome-icon icon="external-link-alt" />
              Open VPN Settings
            </button>
          </div>
        </div>
        
        <div class="step">
          <div class="step-number">2</div>
          <div class="step-content">
            <h6>Select KOVO Home Mobile</h6>
            <p>Find "{{ appName || 'KOVO Home Mobile' }}" in the VPN list and tap the settings gear icon</p>
          </div>
        </div>
        
        <div class="step">
          <div class="step-number">3</div>
          <div class="step-content">
            <h6>Enable Always-on VPN</h6>
            <p>Toggle on "Always-on VPN" to automatically reconnect when network changes</p>
          </div>
        </div>
        
        <div class="step">
          <div class="step-number">4</div>
          <div class="step-content">
            <h6>Block Connections Without VPN</h6>
            <p>Toggle on "Block connections without VPN" to prevent data leaks when VPN is disconnected</p>
            <div class="warning-note">
              <font-awesome-icon icon="exclamation-triangle" />
              <span>This will block all internet if VPN fails to connect</span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="version-specific" v-if="showVersionSpecific">
        <h6>Device-Specific Instructions</h6>
        <div class="version-tabs">
          <button 
            v-for="version in androidVersions" 
            :key="version.id"
            @click="selectedVersion = version.id"
            :class="{ active: selectedVersion === version.id }"
            class="version-tab"
          >
            {{ version.name }}
          </button>
        </div>
        
        <div class="version-content">
          <div v-for="version in androidVersions" :key="version.id" v-show="selectedVersion === version.id">
            <ul>
              <li v-for="instruction in version.instructions" :key="instruction">{{ instruction }}</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="fallback-info">
        <div class="fallback-header">
          <font-awesome-icon icon="shield-alt" />
          <h6>App-Level Protection</h6>
        </div>
        <p>
          Even without system-level always-on VPN, this app provides:
        </p>
        <ul class="feature-list">
          <li><font-awesome-icon icon="check" /> Automatic reconnection on network changes</li>
          <li><font-awesome-icon icon="check" /> Connection blocking when enabled</li>
          <li><font-awesome-icon icon="check" /> Boot-time auto-start</li>
          <li><font-awesome-icon icon="check" /> Background connection monitoring</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { WireGuard } from '@/plugins/wireguard.js';

export default {
  name: 'VpnSettingsInstructions',
  props: {
    appName: {
      type: String,
      default: 'KOVO Home Mobile'
    },
    showVersionSpecific: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedVersion: 'android12',
      androidVersions: [
        {
          id: 'android12',
          name: 'Android 12+',
          instructions: [
            'Open Settings → Network & internet → VPN',
            'Tap the gear icon next to KOVO Home Mobile',
            'Enable "Always-on VPN" and "Block connections without VPN"'
          ]
        },
        {
          id: 'android11',
          name: 'Android 11',
          instructions: [
            'Open Settings → Network & internet → Advanced → VPN',
            'Select KOVO Home Mobile and tap the settings gear',
            'Toggle "Always-on VPN" and "Block connections without VPN"'
          ]
        },
        {
          id: 'android10',
          name: 'Android 10',
          instructions: [
            'Open Settings → Network & internet → VPN',
            'Tap KOVO Home Mobile, then tap the gear icon',
            'Enable "Always-on VPN" and "Block connections without VPN"'
          ]
        },
        {
          id: 'samsung',
          name: 'Samsung',
          instructions: [
            'Open Settings → Connections → More connection settings → VPN',
            'Select KOVO Home Mobile and tap Settings',
            'Enable "Always-on VPN" and "Block connections without VPN"'
          ]
        }
      ]
    };
  },
  methods: {
    async openVpnSettings() {
      try {
        const result = await WireGuard.openVpnSettings();
        if (!result.success) {
          this.$emit('error', 'Could not open VPN settings automatically. Please open Settings manually.');
        }
      } catch (error) {
        this.$emit('error', 'Could not open VPN settings: ' + error.message);
      }
    }
  }
};
</script>

<style scoped>
.vpn-instructions {
  background: var(--color-card);
  border-radius: 12px;
  padding: 20px;
  margin: 15px 0;
}

.instruction-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.info-icon {
  color: var(--color-secondary-button);
  font-size: 1.2em;
}

.instruction-header h5 {
  margin: 0;
  color: var(--text-primary);
  font-weight: 600;
}

.instruction-intro {
  color: #6c757d;
  margin-bottom: 20px;
  line-height: 1.5;
}

.instruction-steps {
  margin-bottom: 25px;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--text-primary);
}

.step:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.step-number {
  background: var(--color-primary-button);
  color: var(--text-primary);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9em;
  flex-shrink: 0;
}

.step-content h6 {
  margin: 0 0 5px 0;
  color: var(--text-primary);
  font-weight: 600;
}

.step-content p {
  margin: 0 0 10px 0;
  color: #6c757d;
  line-height: 1.4;
}

.quick-action-btn {
  background: var(--color-secondary-button);
  color: var(--text-primary);
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.9em;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.quick-action-btn:hover {
  background: var(--color-secondary-button);
}

.warning-note {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  padding: 8px 10px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85em;
  color: #856404;
}

.version-specific {
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid var(--text-primary);
}

.version-specific h6 {
  margin: 0 0 15px 0;
  color: var(--text-primary);
  font-weight: 600;
}

.version-tabs {
  display: flex;
  gap: 5px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.version-tab {
  background: var(--color-card);
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85em;
  cursor: pointer;
  transition: all 0.2s;
}

.version-tab.active {
  background: var(--color-primary-button);
  color: var(--text-primary);
}

.version-tab:hover {
  background: var(--color-primary-button);
  color: var(--text-primary);
}

.version-content ul {
  margin: 0;
  padding-left: 20px;
}

.version-content li {
  margin-bottom: 5px;
  color: #6c757d;
  font-size: 0.9em;
}

.fallback-info {
  background: var(--color-card);
  border: 1px solid var(--color-card-border);
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
}

.fallback-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.fallback-header h6 {
  margin: 0;
  color: var(--color-secondary-button);
  font-weight: 600;
}

.fallback-info p {
  margin: 0 0 10px 0;
  color: var(--color-secondary-button);
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-list li {
  color: var(--color-secondary-button);
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9em;
}

.feature-list li svg {
  color: #28a745;
  font-size: 0.8em;
}
</style>